import { FetchPolicy, useQuery } from '@apollo/client';
import { TConnectionItem, subscribe } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { useEffect, useMemo } from 'react';
import { client } from '../../config/api';
import { onSubscriptionData } from '../../config/cache';
import {
  GetEmployeeDocument,
  ListOneCompanyEmployeesDocument,
  OnAnyEmployeeChangedDocument,
  TDependantFieldsFragment,
  TEmployeeNewFieldsFragment,
  TGetEmployeeQuery,
  TGetEmployeeQueryVariables,
  TListAllEmployeesQuery,
  TListAllEmployeesQueryVariables,
  TListOneCompanyEmployeesQuery,
  TListOneCompanyEmployeesQueryVariables,
  TListOneEmployeeQuery,
  TListOneEmployeeQueryVariables,
  TListOneOfficeEmployeesQuery,
  TListOneOfficeEmployeesQueryVariables,
  TOnAnyEmployeeChangedSubscription,
  TOnAnyEmployeeChangedSubscriptionVariables,
  useListAllEmployeesLazyQuery,
  useListOneCompanyEmployeesLazyQuery,
  useListOneEmployeeLazyQuery,
  useListOneOfficeEmployeesLazyQuery
} from '../../generated/graphql';
import { TAddressType } from '../addresses/queries';
import { TOfficeType } from '../office/queries';
import { useMap } from '../shared/useMap';
import { useQueryAll } from '../shared/useQueryAll';

const onEmployeeSubscriptionData = async (data?: TOnAnyEmployeeChangedSubscription, vars?: TOnAnyEmployeeChangedSubscriptionVariables) => {
  const { id, companyId, _deleted } = data?.onAnyEmployeeChanged || {};

  if (_deleted) return onSubscriptionData(data as Record<string, any>, vars);
  if (!id || !companyId) return;

  const response = await client.query<TGetEmployeeQuery, TGetEmployeeQueryVariables>({
    query: GetEmployeeDocument,
    variables: {
      id,
      companyId
    }
  });

  const employeeData = response.data.getEmployee;

  if (employeeData) {
    const subscriptionData = {
      onAnyEmployeeChanged: employeeData as TConnectionItem
    };

    // CHANGED DATA FOR QUERIES THAT DEPEND ON NO VARIABLE (SINCE VARS SHOULD BE AN EMPTY OBJECT IN THIS SITUATION)
    onSubscriptionData(subscriptionData, vars);

    // CHANGED DATA FOR ADDRESS TOO!
    onSubscriptionData(
      {
        onAnyAddressChanged: employeeData.address as TConnectionItem
      },
      vars
    );
  }
};

let onAnyEmployeeChangedSubscribed = false;

export const useSubscribeToEmployeeChanged = () => {
  useEffect(() => {
    if (!onAnyEmployeeChangedSubscribed) {
      subscribe({
        client,
        query: OnAnyEmployeeChangedDocument,
        onSubscriptionData: (data, vars) => {
          return onEmployeeSubscriptionData(data as TOnAnyEmployeeChangedSubscription, vars as TOnAnyEmployeeChangedSubscriptionVariables);
        }
      });

      onAnyEmployeeChangedSubscribed = true;
    }
  }, []);
};

// LIST

// export const useFullEmployees = <T extends TListEmployeeFieldsFragment>(employees: T[]) => {
//   const { data: companiesMap, error: companiesError, loading: companiesLoading } = useAllCompaniesMap();

//   const { data: officesMap, error: officesError, loading: officesLoading } = useAllOfficesMap();

//   const { data: addressesMap, error: addressesError, loading: addressesLoading } = useAllAddressesMap();

//   const fullEmployees = useMemo(() => {
//     return employees.map((e) => {
//       return {
//         ...e,
//         company: e.companyId ? companiesMap[e.companyId] : undefined,
//         office: e.officeId ? officesMap[e.officeId] : undefined,
//         address: addressesMap[`${import.meta.env.REACT_APP_EMPLOYEES_TABLE_NAME}|${e.id}`],
//       };
//     });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [addressesMap, companiesMap, officesMap, JSON.stringify(employees)]);

//   return {
//     data: fullEmployees,
//     error: companiesError || officesError || addressesError,
//     loading: companiesLoading || officesLoading || addressesLoading,
//   };
// };

export const useCompanyQueryById = (companyId: string) => {
  useSubscribeToEmployeeChanged();
  const { data, loading, error } = useQuery(ListOneCompanyEmployeesDocument, { variables: { companyId } });
  return { data, loading, error };
};

export const useQueryAllEmployeesOld = () => {
  useSubscribeToEmployeeChanged();

  const { data, error, loading } = useQueryAll<TListAllEmployeesQuery, TListAllEmployeesQueryVariables>({
    useQuery: useListAllEmployeesLazyQuery,
    variables: { isScan: true }
  });

  const employees = useMemo(() => data?.listAllEmployees.items || [], [data]);

  return {
    data: employees as TEmployeeType[],
    error: error,
    loading: loading
  };
};
export const useQueryAllEmployees = () => {
  useSubscribeToEmployeeChanged();

  const { data, error, loading, nextToken, fetchMore } = useQueryAll<TListAllEmployeesQuery, TListAllEmployeesQueryVariables>({
    useQuery: useListAllEmployeesLazyQuery,
    variables: { limit: 100, isScan: true },
    isScroll: true
  });

  const employees = useMemo(() => data?.listAllEmployees.items || [], [data]);

  return {
    data: employees as TEmployeeType[],
    error,
    loading,
    nextToken,
    fetchMore
  };
};

export const useQueryAllCompanyEmployeesWithDependants = (variables: TListOneCompanyEmployeesQueryVariables, fetchPolicy?: FetchPolicy) => {
  useSubscribeToEmployeeChanged();

  const { data, error, loading } = useQueryAll<TListOneCompanyEmployeesQuery, TListOneCompanyEmployeesQueryVariables>({
    useQuery: useListOneCompanyEmployeesLazyQuery,
    variables,
    fetchPolicy
  });

  // const {
  //   data: employees,
  //   error: fullError,
  //   loading: fullLoading,
  // } = useFullEmployees<TListEmployeeFieldsFragment>(_.compact(data.listCompanyEmployees.items));

  const employees = useMemo(() => data?.listOneCompanyEmployees.items || [], [data]);

  return {
    data: employees as TEmployeeType[],
    error: error,
    loading: loading
  };
};

export const useAllEmployeesMap = () => {
  const { data, error, loading } = useQueryAllEmployeesOld();

  const employees = (data ?? []) as TEmployeeType[];

  const employeesMap = useMap<(typeof employees)[number]>(employees);

  return {
    data: employeesMap,
    error,
    loading: loading || employeesMap == null
  };
};

// ITEMS

/*export const useFragmentOrFetchEmployee = (variables: TGetEmployeeQueryVariables) => {
  useSubscribeToEmployeeChanged();

  const { data, error, loading } = useFragmentOrFetch<
    TGetEmployeeFragment,
    TGetEmployeeQuery,
    TGetEmployeeQueryVariables
  >({
    fragmentDoc: GetEmployeeFragmentDoc,
    useLazyQuery: useGetEmployeeLazyQuery,
    variables,
    __typename: 'Employee',
  });

  const employee = Array.isArray(data) ? data[0] : data;

  return {
    data: employee as TEmployeeType,
    error: error,
    loading: loading,
  };
};*/

// =========================
// NEW
// =========================

export type TEmployeeType = TEmployeeNewFieldsFragment & {
  company?: Record<string, any>;
  office?: TOfficeType;
  address?: TAddressType;
  dependants?: { items?: Array<TDependantFieldsFragment> };
};

// export const useQueryListAllEmployees = () => {
//   useSubscribeToEmployeeChanged();

//   const { data, error, loading } = useQueryAll<TListAllEmployeesQuery, TListAllEmployeesQueryVariables>({
//     useQuery: useListAllEmployeesLazyQuery,
//     variables: { isScan: true },
//   });

//   const employees = useMemo(() => data.listAllEmployees.items || [], [data]);

//   return {
//     data: employees as TEmployeeType[],
//     error,
//     loading,
//   };
// };

export const useQueryOneEmployee = (variables: TListOneEmployeeQueryVariables) => {
  useSubscribeToEmployeeChanged();

  const { data, error, loading } = useQueryAll<TListOneEmployeeQuery, TListOneEmployeeQueryVariables>({
    useQuery: useListOneEmployeeLazyQuery,
    variables
  });

  const employee = useMemo(() => data?.listOneEmployee.items[0] || {}, [data]);

  return {
    data: employee as TEmployeeType,
    error,
    loading
  };
};

export const useQueryOneOfficeEmployees = (variables: TListOneOfficeEmployeesQueryVariables) => {
  useSubscribeToEmployeeChanged();

  const { data, error, loading } = useQueryAll<TListOneOfficeEmployeesQuery, TListOneOfficeEmployeesQueryVariables>({
    useQuery: useListOneOfficeEmployeesLazyQuery,
    variables
  });

  const employees = useMemo(() => data?.listOneOfficeEmployees.items || [], [data]);

  return {
    data: employees as TEmployeeType[],
    error,
    loading
  };
};

export const useQueryOneCompanyEmployees = (variables: TListOneCompanyEmployeesQueryVariables) => {
  useSubscribeToEmployeeChanged();

  const { data, error, loading } = useQueryAll<TListOneCompanyEmployeesQuery, TListOneCompanyEmployeesQueryVariables>({
    useQuery: useListOneCompanyEmployeesLazyQuery,
    variables
  });

  const employees = useMemo(() => data?.listOneCompanyEmployees.items || [], [data]);

  return {
    data: employees as TEmployeeType[],
    error,
    loading
  };
};
